import { FormEvent, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'
import { formatCurrency } from '../../lib/format'

interface ProductImage {
  id: string
  url: string
  filename: string
  content_type: string
  byte_size: number
}

interface Tag {
  id: number
  name: string
}

interface Product {
  id: number
  name: string
  sku: string
  price: number
  stock_quantity: number
  category?: { id: number; name: string }
  track_variants?: boolean
  variant_count?: number
  total_stock?: number
  product_images?: ProductImage[]
  tags?: Tag[]
}

interface Account {
  id: number
  name: string
  slug: string
}

interface Props {
  products: Product[]
  categories: Array<{ id: number; name: string }>
  tags: Tag[]
  account: Account
  permissions?: { can_manage_products: boolean; can_delete: boolean }
  can_manage?: boolean
  can_delete?: boolean
  show_costs?: boolean
  pagination?: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export default function ProductsIndex({ products, categories, tags, account, permissions, can_manage, can_delete, show_costs, pagination }: Props) {
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const importInputRef = useRef<HTMLInputElement>(null)

  // ── Selección múltiple + tags ──
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [tagsModalOpen, setTagsModalOpen] = useState(false)
  const [addTagIds, setAddTagIds] = useState<Set<number>>(new Set())
  const [removeTagIds, setRemoveTagIds] = useState<Set<number>>(new Set())
  const [newTagName, setNewTagName] = useState('')
  const [savingTags, setSavingTags] = useState(false)
  const [tagList, setTagList] = useState<Tag[]>(tags)

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const allPageSelected = products.length > 0 && products.every(p => selected.has(p.id))

  const toggleSelectAll = () => {
    setSelected(prev => {
      const next = new Set(prev)
      if (allPageSelected) {
        products.forEach(p => next.delete(p.id))
      } else {
        products.forEach(p => next.add(p.id))
      }
      return next
    })
  }

  const openTagsModal = () => {
    // Precalcular tags comunes a TODOS los seleccionados (visibles en esta página)
    const selectedProducts = products.filter(p => selected.has(p.id))
    if (selectedProducts.length > 0) {
      const commonTagIds = new Set(selectedProducts[0].tags?.map(t => t.id) ?? [])
      selectedProducts.forEach(p => {
        const ids = new Set(p.tags?.map(t => t.id) ?? [])
        commonTagIds.forEach(tid => { if (!ids.has(tid)) commonTagIds.delete(tid) })
      })
      setRemoveTagIds(new Set())
      setAddTagIds(new Set())
    }
    setTagsModalOpen(true)
  }

  const toggleAddTag = (id: number) => {
    setAddTagIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setRemoveTagIds(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const toggleRemoveTag = (id: number) => {
    setRemoveTagIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setAddTagIds(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const handleCreateTag = () => {
    const name = newTagName.trim().replace(/\s+/g, ' ')
    if (!name) return
    const existing = tagList.find(t => t.name.toLowerCase() === name.toLowerCase())
    if (existing) {
      toggleAddTag(existing.id)
      setNewTagName('')
      return
    }
    router.post(`/${account.slug}/products/create_tag`, { name }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: (page: any) => {
        const created = page.props.tags?.find((t: Tag) => t.name.toLowerCase() === name.toLowerCase())
        if (created) {
          setTagList(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
          toggleAddTag(created.id)
        }
        setNewTagName('')
      },
      onError: () => alert('No se pudo crear el tag'),
    })
  }

  const handleSaveTags = () => {
    if (addTagIds.size === 0 && removeTagIds.size === 0) {
      setTagsModalOpen(false)
      return
    }
    setSavingTags(true)
    router.post(`/${account.slug}/products/bulk_tags`, {
      product_ids: Array.from(selected),
      tag_ids: Array.from(addTagIds),
      remove_tag_ids: Array.from(removeTagIds),
      // Reenviar filtros activos para que el re-render no los pierda
      search: search || undefined,
      category_id: categoryId || undefined,
      page: pagination?.page || undefined,
    }, {
      preserveScroll: true,
      onFinish: () => setSavingTags(false),
      onSuccess: () => {
        setTagsModalOpen(false)
        setSelected(new Set())
        setAddTagIds(new Set())
        setRemoveTagIds(new Set())
      },
    })
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    router.get(`/${account.slug}/products`, { search: value, category_id: categoryId }, { preserveState: true, replace: true })
  }

  const handleCategoryFilter = (value: string) => {
    setCategoryId(value)
    router.get(`/${account.slug}/products`, { search, category_id: value }, { preserveState: true, replace: true })
  }

  const handleDelete = (id: number) => {
    if (confirm('¿Estás seguro de eliminar este producto?')) {
      router.delete(`/${account.slug}/products/${id}`)
    }
  }

  const handleImport = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!importFile || importing) return

    const formData = new FormData()
    formData.append('file', importFile)
    setImporting(true)

    router.post(`/${account.slug}/products/import`, formData, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        setImportFile(null)
        if (importInputRef.current) importInputRef.current.value = ''
      },
      onFinish: () => setImporting(false),
    })
  }

  return (
    <Layout account={account} permissions={permissions}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-[#202020]">Productos</h1>
        <div className="flex flex-wrap gap-2">
          <a href={`/${account.slug}/products/import_template`} className="bg-[#55ACEE] text-white px-4 py-2 rounded hover:bg-[#4a9dd4] transition-colors text-sm">Descargar Plantilla</a>
          <Link href={`/${account.slug}/products/new`} className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded hover:bg-[#e6ad06] transition-colors text-sm font-semibold">Nuevo Producto</Link>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <input type="text" placeholder="Buscar por nombre..." value={search} onChange={(e) => handleSearch(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
          <select value={categoryId} onChange={(e) => handleCategoryFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
            <option value="">Todas las categorías</option>
            {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
          </select>
        </div>
        <form onSubmit={handleImport} encType="multipart/form-data" className="flex flex-col md:flex-row items-center gap-4">
          <input
            ref={importInputRef}
            type="file"
            name="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            className="flex-1"
          />
          <button
            type="submit"
            disabled={!importFile || importing}
            className="bg-[#55ACEE] text-white px-4 py-2 rounded hover:bg-[#4a9dd4] transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
          >
            {importing ? 'Importando...' : 'Importar Excel'}
          </button>
        </form>

        <details className="text-sm text-gray-600 border-t pt-3">
          <summary className="cursor-pointer font-medium text-[#202020] hover:text-[#55ACEE] transition-colors">
            📖 Instrucciones para llenar la plantilla
          </summary>
          <div className="mt-3 space-y-3">
            <div>
              <p className="font-semibold text-[#202020] mb-1">Columnas</p>
              <p>
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Handle</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Nombre</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">SKU</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Precio</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Costo</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Stock</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Categoría</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Descripción</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Opción 1</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Valor 1</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Opción 2</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">Valor 2</span>.
              </p>
            </div>

            <div>
              <p className="font-semibold text-[#202020] mb-1">Producto simple (sin variantes)</p>
              <p>
                Una sola fila con <span className="font-mono text-xs bg-gray-100 px-1 rounded">Handle</span> vacío y
                sin opciones. El stock y el precio van en esa misma fila.
              </p>
            </div>

            <div>
              <p className="font-semibold text-[#202020] mb-1">Producto con variantes (ej. tallas o colores)</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Escribe el mismo <span className="font-mono text-xs bg-gray-100 px-1 rounded">Handle</span> en todas
                  las filas del producto: <strong>una fila por variante</strong>. El orden de las filas no importa.
                </li>
                <li>
                  La <strong>primera fila</strong> define el nombre, categoría, costo,{' '}
                  <strong>precio base (obligatorio)</strong> y el <strong>SKU del producto</strong>{' '}
                  (sin el sufijo de talla, ej. <span className="font-mono text-xs bg-gray-100 px-1 rounded">CB</span>).
                  Esta fila va <strong>sin opciones</strong>.
                </li>
                <li>
                  Cada fila siguiente (mismo <span className="font-mono text-xs bg-gray-100 px-1 rounded">Handle</span>
                  , con opciones) es una variante y lleva su <strong>propio SKU</strong> (ej.{' '}
                  <span className="font-mono text-xs bg-gray-100 px-1 rounded">CB-M</span>,{' '}
                  <span className="font-mono text-xs bg-gray-100 px-1 rounded">CB-L</span>). Así el producto y cada
                  variante quedan con su SKU.
                </li>
                <li>
                  En cada variante llena{' '}
                  <span className="font-mono text-xs bg-gray-100 px-1 rounded">Opción 1</span> /{' '}
                  <span className="font-mono text-xs bg-gray-100 px-1 rounded">Valor 1</span> (ej. Talla / M). Para una
                  segunda dimensión usa <span className="font-mono text-xs bg-gray-100 px-1 rounded">Opción 2</span> /{' '}
                  <span className="font-mono text-xs bg-gray-100 px-1 rounded">Valor 2</span> (ej. Color / Rojo).
                </li>
                <li>
                  Si una variante deja el <span className="font-mono text-xs bg-gray-100 px-1 rounded">Precio</span>{' '}
                  vacío, hereda el precio base del producto.
                </li>
              </ul>
            </div>

            <div>
              <p className="font-semibold text-[#202020] mb-1">Reglas importantes</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  El <span className="font-mono text-xs bg-gray-100 px-1 rounded">SKU</span> debe ser único: no se
                  repite entre productos, variantes ni archivos anteriores.
                </li>
                <li>
                  Una fila con opciones pero <strong>sin Handle</strong> se ignora (variante sin producto).
                </li>
                <li>
                  Las filas de un Handle <strong>sin opciones</strong> (después de la primera) se ignoran.
                </li>
                <li>
                  El resumen al final de la importación indica cuántos productos, variantes y filas ignoradas hubo.
                </li>
              </ul>
            </div>
          </div>
        </details>
      </div>

      {/* Barra de selección múltiple */}
      {selected.size > 0 && (
        <div className="bg-[#202020] text-white px-4 py-3 rounded-lg shadow mb-4 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-semibold">
            {selected.size} producto{selected.size > 1 ? 's' : ''} seleccionado{selected.size > 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            <button onClick={openTagsModal} className="bg-[#FFC107] text-[#202020] px-4 py-1.5 rounded hover:bg-[#e6ad06] transition-colors text-sm font-semibold">
              🏷️ Asignar Tags
            </button>
            <button onClick={() => setSelected(new Set())} className="bg-white/10 text-white px-4 py-1.5 rounded hover:bg-white/20 transition-colors text-sm">
              Limpiar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-[#FFC107] cursor-pointer"
                    title="Seleccionar todos los de esta página"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imagen</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className={selected.has(product.id) ? 'bg-yellow-50' : ''}>
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selected.has(product.id)}
                      onChange={() => toggleSelect(product.id)}
                      className="w-4 h-4 accent-[#FFC107] cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.product_images && product.product_images.length > 0 ? (
                      <img src={product.product_images[0].url} alt={product.name} className="w-10 h-10 object-cover rounded border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-gray-400 text-xs">Sin foto</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{product.category?.name || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {product.name}
                      {product.track_variants && (<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#55ACEE] text-white">Variantes</span>)}
                    </div>
                    {product.tags && product.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {product.tags.slice(0, 3).map(t => (
                          <span key={t.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#FFC107]/20 text-[#202020] border border-[#FFC107]/40">{t.name}</span>
                        ))}
                        {product.tags.length > 3 && (
                          <span className="text-[10px] text-gray-500">+{product.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">{product.sku || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(Number(product.price))}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.track_variants ? (
                      <span className={`px-2 py-1 rounded text-sm ${(product.total_stock || 0) < 10 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{product.total_stock || 0}</span>
                    ) : (
                      <span className={`px-2 py-1 rounded text-sm ${product.stock_quantity < 10 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{product.stock_quantity}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap space-x-2">
                    {product.track_variants && (
                      <Link href={`/${account.slug}/products/${product.id}/variants`} className="text-[#FFC107] hover:underline font-semibold">Variantes</Link>
                    )}
                    <Link href={`/${account.slug}/products/${product.id}/edit`} className="text-[#55ACEE] hover:underline">Editar</Link>
                    <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:underline">Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Paginación */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-4 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => router.get(`/${account.slug}/products`, { search, category_id: categoryId, page: pagination.page - 1 }, { preserveState: true, replace: true })}
              disabled={pagination.page <= 1}
              className="px-4 py-2 text-sm rounded bg-white border border-gray-300 text-[#202020] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página {pagination.page} de {pagination.total_pages}
            </span>
            <button
              onClick={() => router.get(`/${account.slug}/products`, { search, category_id: categoryId, page: pagination.page + 1 }, { preserveState: true, replace: true })}
              disabled={pagination.page >= pagination.total_pages}
              className="px-4 py-2 text-sm rounded bg-white border border-gray-300 text-[#202020] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {/* Modal de asignación masiva de tags */}
      {tagsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#202020]">🏷️ Asignar Tags</h3>
              <button onClick={() => setTagsModalOpen(false)} className="text-gray-400 hover:text-[#202020] text-xl leading-none">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Se aplicará a <strong>{selected.size}</strong> producto{selected.size > 1 ? 's' : ''}.
                El cliente filtra el catálogo web con estos tags.
              </p>

              {/* Crear tag nuevo */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nuevo tag (Enter para crear)..."
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag() } }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#FFC107] text-sm"
                />
                <button onClick={handleCreateTag} disabled={!newTagName.trim()} className="bg-[#55ACEE] text-white px-4 py-2 rounded hover:bg-[#4a9dd4] transition-colors text-sm disabled:opacity-50">
                  Crear
                </button>
              </div>

              {/* Tags existentes */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Tags de la tienda</p>
                {tagList.length === 0 ? (
                  <p className="text-sm text-gray-400">Aún no hay tags. Crea el primero arriba. ✏️</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {tagList.map(tag => {
                      const willAdd = addTagIds.has(tag.id)
                      const willRemove = removeTagIds.has(tag.id)
                      return (
                        <button
                          key={tag.id}
                          onClick={() => (willRemove ? toggleRemoveTag(tag.id) : toggleAddTag(tag.id))}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            willAdd
                              ? 'bg-[#FFC107] border-[#FFC107] text-[#202020]'
                              : willRemove
                                ? 'bg-red-100 border-red-300 text-red-700 line-through'
                                : 'bg-white border-gray-300 text-gray-700 hover:border-[#FFC107]'
                          }`}
                        >
                          {tag.name}
                        </button>
                      )
                    })}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-2">
                  Toca un tag para <span className="text-[#b8860b] font-semibold">asignarlo</span> (amarillo) o{' '}
                  <span className="text-red-600 font-semibold">quitarlo</span> de la selección (rojo, solo si todos los seleccionados lo tienen).
                </p>
              </div>

              {/* Tags comunes → opción quitar */}
              {(() => {
                const selectedProducts = products.filter(p => selected.has(p.id))
                if (selectedProducts.length === 0) return null
                const commonTagIds = new Set(selectedProducts[0].tags?.map(t => t.id) ?? [])
                selectedProducts.forEach(p => {
                  const ids = new Set(p.tags?.map(t => t.id) ?? [])
                  commonTagIds.forEach(tid => { if (!ids.has(tid)) commonTagIds.delete(tid) })
                })
                if (commonTagIds.size === 0) return null
                return (
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Tags comunes a la selección</p>
                    <div className="flex flex-wrap gap-2">
                      {tagList.filter(t => commonTagIds.has(t.id)).map(tag => {
                        const willRemove = removeTagIds.has(tag.id)
                        return (
                          <button
                            key={tag.id}
                            onClick={() => toggleRemoveTag(tag.id)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                              willRemove
                                ? 'bg-red-100 border-red-300 text-red-700 line-through'
                                : 'bg-white border-gray-300 text-gray-700 hover:border-red-300'
                            }`}
                          >
                            {tag.name}
                          </button>
                        )
                      })}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">Rojo tachado = se quitará de todos los seleccionados.</p>
                  </div>
                )
              })()}
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setTagsModalOpen(false)} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors">
                Cancelar
              </button>
              <button
                onClick={handleSaveTags}
                disabled={savingTags || (addTagIds.size === 0 && removeTagIds.size === 0)}
                className="bg-[#FFC107] text-[#202020] px-5 py-2 rounded hover:bg-[#e6ad06] transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingTags ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pantalla de carga para importación */}
      {importing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="inline-block w-10 h-10 border-4 border-[#55ACEE] border-t-transparent rounded-full animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-[#202020] mb-1">Importando productos...</h3>
            <p className="text-sm text-gray-500">Esto puede tomar unos segundos</p>
          </div>
        </div>
      )}
    </Layout>
  )
}
